import { Injectable, NgZone } from "@angular/core";
import { BehaviorSubject, Observable, Subject } from "rxjs";
import { debounceTime, switchMap } from "rxjs/operators";

import { GetUnreadUserNotificationsQuantityUseCase } from "@app/core/notifications/domain/get-unread-user-notifications-quantity.use-case";
import { Capacitor } from "@capacitor/core";
import { PushNotificationsService } from "@app/core/notifications/domain/push-notifications.service";

@Injectable({
  providedIn: "root",
})
export class NotificationsService {
  private readonly notificationsQuantitySubject = new BehaviorSubject<number>(
    0
  );
  readonly notificationsQuantity$: Observable<number> =
    this.notificationsQuantitySubject.asObservable();

  private updateTrigger$ = new Subject<void>();

  constructor(
    private getUnreadNotifications: GetUnreadUserNotificationsQuantityUseCase,
    private pushNotificationsService: PushNotificationsService,
    private ngZone: NgZone
  ) {
    this.initializeService();
  }

  /**
   * Initialize the service, setting up listeners and triggering the initial update.
   */
  initializeService() {
    if (!Capacitor.isNativePlatform()) return;
    this.initializeUpdateListener();
    this.updateQuantity();
    this.setupPushNotifications();
  }

  /**
   * Initializes the listener for update triggers.
   * Uses debounceTime to wait for 300 milliseconds after the last trigger
   * before executing the getUnreadNotifications use case and updating
   * notificationsQuantitySubject with the result.
   */
  private initializeUpdateListener() {
    this.updateTrigger$
      .pipe(
        debounceTime(300),
        switchMap(() => this.getUnreadNotifications.execute())
      )
      .subscribe((quantity) =>
        this.notificationsQuantitySubject.next(quantity)
      );
  }

  /**
   * Triggers an update of the notifications quantity.
   * Call this method whenever you want to initiate an update.
   */
  updateQuantity() {
    this.updateTrigger$.next();
  }

  /**
   * Set up listeners for push notifications, triggering an update on notification received.
   */
  private setupPushNotifications() {
    this.pushNotificationsService.onNotificationReceived$.subscribe(() =>
      this.ngZone.run(() => this.updateQuantity())
    );
  }
}
